import { useState, useEffect } from 'react';
import { 
    fetchAnalyticsAll, 
    fetchAnalyticsMetrics, 
    fetchAnalyticsTeam, 
    exportAnalyticsReport 
} from '../services/analyticsService';

/**
 * Hook to manage analytics data fetching and state.
 */
export const useAnalyticsData = (timeRange) => {
    const [loading, setLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [error, setError] = useState(null);
    const [trends, setTrends] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [team, setTeam] = useState([]);

    const formatRange = (range) => {
        return range.toLowerCase().replace(/ /g, "_");
    };

    const handleExport = async () => {
        setIsExporting(true);
        setError(null);
        try {
            const formattedRange = formatRange(timeRange);
            const response = await exportAnalyticsReport(formattedRange);

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `performance_report_${formattedRange}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error("Export error:", err);
            setError("Failed to export report. Please try again.");
        } finally {
            setIsExporting(false);
        }
    };

    const loadDashboardData = async () => {
        setLoading(true);
        setError(null);
        const formattedRange = formatRange(timeRange);
        const commonParams = { time_range: formattedRange };

        try {
            const [trendsRes, metricsRes, teamRes] = await Promise.all([
                fetchAnalyticsAll(commonParams),
                fetchAnalyticsMetrics(commonParams),
                fetchAnalyticsTeam(commonParams)
            ]);

            setTrends(trendsRes.data);
            setMetrics(metricsRes.data);
            setTeam(Array.isArray(teamRes.data) ? teamRes.data : []);
        } catch (err) {
            console.error("Dashboard fetch error:", err);
            setError("Failed to load analytics data. Using latest available data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadDashboardData();
    }, [timeRange]);

    return {
        loading,
        error,
        trends,
        metrics,
        team,
        isExporting,
        handleExport
    };
};
